import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import paymentMethodService from '../services/paymentMethodService';
import { useAuth } from '../context/AuthContext';

// Hook to get payment method query options
export const getPaymentMethodQueryOptions = (id: number) => ({
  queryKey: ['payment-method', id],
  queryFn: () => paymentMethodService.getPaymentMethod(id),
  enabled: !!id,
  staleTime: 1000 * 60 * 5, // 5 minutes
});

// Custom hook for payment methods
export const usePaymentMethods = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  
  // Fetch payment methods list only when authenticated
  const { 
    data: response, 
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => paymentMethodService.getPaymentMethods(),
    enabled: isAuthenticated, // Only fetch when authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  // Extract payment methods from the response
  const paymentMethods = response?.data?.data || [];
  
  // Attach payment method
  const attachPaymentMethod = useMutation({
    mutationFn: paymentMethodService.attachPaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });
  
  // Delete payment method
  const deletePaymentMethod = useMutation({
    mutationFn: paymentMethodService.deletePaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });
  
  // Set default payment method
  const setDefaultPaymentMethod = useMutation({
    mutationFn: paymentMethodService.setDefaultPaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });
  
  // Create payment method with Stripe
  const createPaymentMethod = useMutation({
    mutationFn: ({ paymentMethodData, name }: { 
      paymentMethodData: {
        type: 'card';
        card: any;
        billing_details?: {
          name?: string;
          email?: string;
          phone?: string;
          address?: {
            city?: string;
            country?: string;
            line1?: string;
            line2?: string;
            postal_code?: string;
            state?: string;
          };
        };
      },
      name: string
    }) => paymentMethodService.createPaymentMethod(paymentMethodData, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });

  // Refetch payment methods
  const refetchPaymentMethods = () => {
    queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
  };
  
  return {
    paymentMethods,
    isLoading,
    error,
    isError,
    refetch: refetchPaymentMethods,
    attachPaymentMethod: attachPaymentMethod.mutate,
    isPendingAttach: attachPaymentMethod.isPending,
    attachError: attachPaymentMethod.error,
    createPaymentMethod: createPaymentMethod.mutate,
    isPendingCreate: createPaymentMethod.isPending,
    createError: createPaymentMethod.error,
    deletePaymentMethod: deletePaymentMethod.mutate,
    isPendingDelete: deletePaymentMethod.isPending,
    deleteError: deletePaymentMethod.error,
    setDefaultPaymentMethod: setDefaultPaymentMethod.mutate,
    isPendingSetDefault: setDefaultPaymentMethod.isPending,
    setDefaultError: setDefaultPaymentMethod.error
  };
};

// Custom hook for a single payment method
export const usePaymentMethod = (id: number) => {
  return useQuery(getPaymentMethodQueryOptions(id));
};

export default usePaymentMethods; 